import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import type { BenchmarkJobType } from '@chatsdet/types';

export type BenchmarkConfig = {
  id: string;
  name: string;
  description: string | null;
  jobType: BenchmarkJobType;
  provider: string | null;
  model: string | null;
  promptVersion: string | null;
  jobConfig: Record<string, unknown> | null;
  isDefault: boolean;
  version: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export function useBenchmarkConfigsQuery(params?: {
  jobType?: BenchmarkJobType;
  isDefault?: boolean;
}) {
  return useQuery({
    queryKey: ['benchmark-configs', params],
    queryFn: async (): Promise<{ configs: BenchmarkConfig[] }> => {
      const searchParams = new URLSearchParams();
      if (params?.jobType) searchParams.set('jobType', params.jobType);
      if (params?.isDefault !== undefined) searchParams.set('isDefault', String(params.isDefault));

      const res = await fetch(
        `${API_URL}/superadmin/benchmark/configs?${searchParams.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw await toApiError(res, 'Failed to fetch benchmark configs');
      }
      return res.json();
    },
  });
}

export function useCreateBenchmarkConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      jobType: BenchmarkJobType;
      provider?: string;
      model?: string;
      promptVersion?: string;
      jobConfig?: Record<string, unknown>;
      isDefault?: boolean;
    }) => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/configs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error ?? 'Failed to create config');
      }
      return data as { config: BenchmarkConfig };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-configs'] }).catch(() => {});
    },
  });
}

export function useUpdateBenchmarkConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: {
      id: string;
      name?: string;
      description?: string | null;
      provider?: string | null;
      model?: string | null;
      promptVersion?: string | null;
      jobConfig?: Record<string, unknown> | null;
      isDefault?: boolean;
    }) => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/configs/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error ?? 'Failed to update config');
      }
      return data as { config: BenchmarkConfig };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-configs'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['benchmark-config'] }).catch(() => {});
    },
  });
}

export function useDeleteBenchmarkConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/configs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error ?? 'Failed to delete config');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-configs'] }).catch(() => {});
    },
  });
}

export function useSetDefaultConfigMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/configs/${id}/set-default`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error ?? 'Failed to set default config');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-configs'] }).catch(() => {});
    },
  });
}
